import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import './ColorSchemeSection.scss'

const colorFieldsConfig = [
  {
    name: 'primaryColor',
    label: 'Primary (Teal)',
    presets: ['#009A97', '#0077CC', '#7B2D8B', '#E24B4A', '#27AE60']
  },
  {
    name: 'accentColor',
    label: 'Accent (Amber / Badge)',
    presets: ['#E8971A', '#F5A623', '#E74C3C', '#8E44AD', '#2ECC71']
  },
  {
    name: 'backgroundColor',
    label: 'Page Background',
    presets: ['#F4F6F8', '#FFFFFF', '#F1EFE8', '#EAF4F4'],
  },
  {
    name: 'topbarBgColor',
    label: 'Topbar Background',
    presets: ['#009A97', '#1a1a18', '#2C3E50', '#7B2D8B', '#FFFFFF'],
  },
  {
    name: 'topbarTextColor',
    label: 'Topbar Text',
    presets: ['#FFFFFF', '#1a1a18', '#E0F5F5'],
  },
  {
    name: 'priceColor',
    label: 'Price Color',
    presets: []
  },
  {
    name: 'itemColor',
    label: 'Item Title Color',
    presets: []
  }
]

const fieldMap = {
  siteTitle: 'titleText',
  primaryColor: 'primaryColor',
  accentColor: 'accentColor',
  backgroundColor: 'pageBackgroundColor',
  topbarBgColor: 'topbarBackgroundColor',
  topbarTextColor: 'topbarTextColor',
  priceColor: 'priceColor',
  itemColor: 'itemTitleColor',
}

export default function ColorSchemeSection() {
  const { watch, setValue } = useFormContext()

  const colors = {
    siteTitle: watch('titleText') || '',
    primaryColor: watch('primaryColor') || '#009A97',
    accentColor: watch('accentColor') || '#E8971A',
    backgroundColor: watch('pageBackgroundColor') || '#F4F6F8',
    topbarBgColor: watch('topbarBackgroundColor') || '#009A97',
    topbarTextColor: watch('topbarTextColor') || '#FFFFFF',
    priceColor: watch('priceColor') || '#009A97',
    itemColor: watch('itemTitleColor') || '#009A97',
  }

  const handleColorChange = useCallback((name, value) => {
    const formFieldName = fieldMap[name]
    if (formFieldName) {
      setValue(formFieldName, value)
    }
  }, [setValue])

  const handleHexChange = useCallback((name, value) => {
    let formatted = value
    if (value && value[0] !== '#') {
      formatted = `#${value}`
    }
    const formFieldName = fieldMap[name]
    if (formFieldName) {
      setValue(formFieldName, formatted)
    }
  }, [setValue])

  const handleResetColors = useCallback(() => {
    setValue('titleText', 'co·bee')
    setValue('primaryColor', '#009A97')
    setValue('accentColor', '#E8971A')
    setValue('pageBackgroundColor', '#F4F6F8')
    setValue('topbarBackgroundColor', '#009A97')
    setValue('topbarTextColor', '#FFFFFF')
    setValue('priceColor', '#009A97')
    setValue('itemTitleColor', '#009A97')
  }, [setValue])

  return (
    <div className='settings-section'>
      <div className='settings-section-header'>
        <span className='settings-section-title'>Color Scheme</span>
      </div>
      <div className='settings-section-body'>
        <div className='settings-helper' style={{ marginBottom: '2px' }}>
          Color changes apply to both member-facing event pages and the admin interface.
        </div>
        <div className='settings-site-container' style={{ marginBottom: 0 }}>
          <div className='settings-row-label' style={{ marginBottom: '10px' }}>
            Site Title
          </div>
          <div className='settings-field-container'>
            <div className='settings-field' style={{ flex: 1, minWidth: '180px', marginBottom: 0 }}>
              <label htmlFor='siteTitle' style={{ textTransform: 'none' }}>
                Title Text
              </label>
              <input
                id='siteTitle'
                className='settings-input'
                type='text'
                placeholder='e.g. co·bee'
                value={colors.siteTitle}
                onChange={(e) => handleColorChange('siteTitle', e.target.value)}
              />
              <span className='settings-helper'>
                Replaces the &quot;co·bee&quot; header across the platform.
              </span>
            </div>
            <div id='title-preview-box' style={{ background: colors.primaryColor }}>
              <span id='site-title-preview' style={{ color: colors.topbarTextColor }}>
                {colors.siteTitle}
              </span>
            </div>
          </div>
        </div>

        <div className='settings-row-label' style={{ marginBottom: '12px' }}>
          Colors
        </div>
        <div className='settings-color-grid'>
          {colorFieldsConfig.map((field) => {
            const value = colors[field.name] || ''
            return (
              <div key={field.name} className='settings-color-field'>
                <label>{field.label}</label>
                <div className='settings-color-preview'>
                  <input
                    name={field.name}
                    type='color'
                    className='settings-color-swatch'
                    value={value}
                    onChange={(e) => handleColorChange(field.name, e.target.value)}
                  />
                  <input
                    name={field.name}
                    type='text'
                    className='settings-color-input-hex'
                    maxLength={7}
                    value={value}
                    onChange={(e) => handleHexChange(field.name, e.target.value)}
                  />
                </div>
                {field.presets && field.presets.length > 0 && (
                  <div className='settings-color-presets'>
                    {field.presets.map((preset) => (
                      <button
                        key={preset}
                        type='button'
                        className='settings-color-preset'
                        style={{ backgroundColor: preset }}
                        onClick={() => handleColorChange(field.name, preset)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <button
          type='button'
          className='settings-secondary-btn'
          style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}
          onClick={handleResetColors}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 .49-4.95"></path></svg>
          Reset to Default
        </button>
      </div>
    </div>
  )
}
